import React, { ChangeEvent, useCallback, useEffect } from "react";

import { fork } from "@formant/core/common/fork";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { AnalyticsAggregateLevel } from "@formant/core/model/AnalyticsAggregateLevel";
import { analyticsAggregateLevels } from "@formant/core/model/analyticsAggregateLevels";
import { AnalyticsChartType } from "@formant/core/model/AnalyticsChartType";
import { IAnalyticsLayout } from "@formant/core/model/IAnalyticsLayout";
import { IAnalyticsModule } from "@formant/core/model/IAnalyticsModule";
import { IAnalyticsModuleParameters } from "@formant/core/model/IAnalyticsModuleParameters";
import { IEventColumn } from "@formant/core/model/IEventColumn";
import { IObserveLayout } from "@formant/core/model/IObserveLayout";
import { ISqlQuery } from "@formant/core/model/ISqlQuery";
import { ISqlResult } from "@formant/core/model/ISqlResult";
import { IStreamColumn } from "@formant/core/model/IStreamColumn";
import { ITableColumn } from "@formant/core/model/ITableColumn";
import { ITaskReportColumn } from "@formant/core/model/ITaskReportColumn";
import { primary } from "@formant/ui/component/common/Colors";
import { TextField } from "@formant/ui/component/common/TextField";

import { analyticsStore, appStore } from "../../../stores";
import { SelectField } from "../../common/SelectField";
import { Switch } from "../../common/Switch";
import { ITab } from "../../common/Tabs/ITab";
import { Label } from "../../JsonSchemaForm/Label";
import { GenericPicker } from "../../StreamPicker/GenericPicker";
import { AdvancedEditor } from "./AdvancedEditor";
import { ChartSelector } from "./ChartSelector";
import {
    Header,
    SmallFormContainer,
    SubContainer,
    TabsContainer,
} from "./EditAnalytisModule.styles";

interface IEditAnalyticsModule {
    layout: IAnalyticsLayout;
    moduleId: string | undefined;
    onChangeLayout: (
        layout: IAnalyticsLayout | IObserveLayout,
    ) => Promise<void>;
}

const getResult = async (
    query: ISqlQuery | undefined,
): Promise<ISqlResult | undefined> => {
    if (!query) {
        return undefined;
    }
    try {
        return (await analyticsStore.fetchListMatching(query)) || undefined;
    } catch (error) {
        if (!(error instanceof ResponseError)) {
            appStore.setSnackbar(
                "Unexpected error occurred" + JSON.stringify(error),
            );
            return;
        }
        const responseError = narrowError(error, ResponseError);
        const { statusCode } = responseError;
        if (statusCode === 422) {
            appStore.setSnackbar("Invalid SQL Statement" + responseError);
        } else if (statusCode === 413) {
            appStore.setSnackbar("Too much data requested");
        }
    }
};

export function EditAnalyticsModule(props: IEditAnalyticsModule) {
    const { layout, moduleId: editingModuleId } = props;

    if (!editingModuleId) {
        return null;
    }

    const moduleKey = `analytics-module:${editingModuleId}`;
    const moduleLayout = layout[`analytics-module:${editingModuleId}`];

    if (!moduleLayout?.configuration) {
        return null;
    }

    const params = moduleLayout?.configuration
        ?.parameters as IAnalyticsModuleParameters;
    const query = params?.query;
    const isAdvanced = query?.type === "advanced";
    const tableColumns = query?.tableColumns || [];

    const [showAdvanced, setShowAdvanced] = React.useState<boolean>(false);
    const [result, setResult] = React.useState<ISqlResult | undefined>(
        undefined,
    );

    const [showAxisControls, setShowAxisControls] =
        React.useState<boolean>(false);

    useEffect(() => {
        setShowAdvanced(isAdvanced);
        setShowAxisControls(
            !!params.configuration?.advancedChartSettings?.xAxis,
        );
    }, [params]);

    const executeSqlString = async (
        sqlQuery: string,
    ): Promise<ISqlResult | undefined> => {
        return await getResult({
            type: "advanced",
            ...query,
            sqlQuery,
        } as unknown as ISqlQuery);
    };

    const loadData = useCallback(async () => {
        setResult(await getResult(query));
    }, [query?.type, query?.sqlQuery, query?.tableColumns]);

    useEffect(() => {
        setShowAdvanced(isAdvanced);
    }, [query?.type]);

    useEffect(() => {
        fork(loadData());
    }, [loadData]);

    const configuration = params?.configuration || {
        chartType: "line",
        aggregateLevel: "day",
    };

    const name = params?.AnalyticsModuleName;
    const hasEditedName = params.configuration?.editedName;
    const deviceIds = query?.deviceIds || [];
    const chartType = configuration?.chartType || "line";
    const deviceLength = (query?.deviceIds || []).length;
    const allowSplitByDevice =
        deviceLength > 1 && deviceLength < 8 && tableColumns.length < 2;
    const aggregateLevel = query?.aggregateLevel;
    const sqlText =
        !query?.sqlQuery || query?.sqlQuery === ""
            ? result?.sqlText
            : query?.sqlQuery;
    // just to refresh in the background
    analyticsStore.listFilteredStreams({ deviceIds });
    analyticsStore.listTaskReports({ deviceIds });

    const handleUpdate = (updates: Partial<IAnalyticsModule>) => {
        const newParams: IAnalyticsModuleParameters = {
            query,
            AnalyticsModuleDefinitionId:
                params?.AnalyticsModuleDefinitionId || updates?.id,
            AnalyticsModuleName:
                updates?.name !== undefined
                    ? updates?.name
                    : params?.AnalyticsModuleName,
            configuration: params?.configuration,
        };

        if (updates.configuration) {
            newParams.configuration = {
                ...params?.configuration,
                ...updates?.configuration,
            };
        }

        if (updates.name !== undefined && newParams?.configuration) {
            newParams.configuration.editedName = true;
        }

        if (updates.query) {
            newParams.query = {
                ...query,
                ...updates.query,
                timeDimension: newParams.configuration?.chartType !== "summary",
            };
        }

        moduleLayout.configuration = {
            ...moduleLayout.configuration,
            parameters: newParams,
        };
        const newLayout: IAnalyticsLayout = {
            ...layout,
            [moduleKey]: moduleLayout,
        };

        fork(props.onChangeLayout(newLayout));
    };

    const handleChangeXAxis = (event: React.ChangeEvent<HTMLSelectElement>) => {
        handleUpdate({
            configuration: {
                advancedChartSettings: {
                    ...params.configuration?.advancedChartSettings,
                    xAxis: event.target.value,
                },
            },
        });
    };

    const handleChangePartitionBy = (
        event: React.ChangeEvent<HTMLSelectElement>,
    ) => {
        const advancedSettings = params.configuration?.advancedChartSettings;
        handleUpdate({
            configuration: {
                advancedChartSettings: {
                    ...advancedSettings,
                    partitionBy: event.target.value,
                },
            },
        });
    };

    const handleChangeName = (event: ChangeEvent<HTMLInputElement>) => {
        handleUpdate({ name: event.target.value });
    };

    const toStream = (tableColumn: ITableColumn): IStreamColumn => {
        return tableColumn as IStreamColumn;
    };

    const toTaskReport = (tableColumn: ITableColumn): ITaskReportColumn => {
        return tableColumn as ITaskReportColumn;
    };

    const toEvent = (tableColumn: ITableColumn): IEventColumn => {
        return tableColumn as IEventColumn;
    };

    const handleChangeTableColumns = (inputTableColumns: ITableColumn[]) => {
        const input: ITableColumn[] = inputTableColumns.map(_ => ({
            aggregateType: "avg",
            ..._,
        }));
        const last = [...input].pop();

        let columnName = name;
        switch (last?.type) {
            case "stream":
                columnName = toStream(last)?.streamName;
                break;
            case "task-report":
                columnName = toTaskReport(last)?.columnName;
                break;
            case "event":
                columnName = toEvent(last)?.eventType;
                break;
            default:
                columnName = name;
        }

        handleUpdate({
            name: hasEditedName ? undefined : columnName,
            query: {
                ...query,
                tableColumns: input,
                aggregateLevel: aggregateLevel || "date",
                type: "stream",
            },
        });
    };

    const onChangeAggregateLevel = (
        level: AnalyticsAggregateLevel | undefined,
    ) => {
        handleUpdate({
            query: {
                ...query,
                aggregateLevel: level,
                timeDimension: chartType !== "summary",
            },
        });
    };

    const handleChangeAggregateLevel = (
        event: React.ChangeEvent<HTMLSelectElement>,
    ) => {
        onChangeAggregateLevel(
            event.target.value !== "none"
                ? (event.target.value as AnalyticsAggregateLevel)
                : undefined,
        );
    };

    const handleSplitByDevice = (enabled: boolean) => {
        handleUpdate({
            query: {
                groupByDevice: enabled,
            },
        });
    };

    const handleFillGaps = (enabled: boolean) => {
        handleUpdate({
            query: {
                fillGaps: enabled,
            },
        });
    };

    const handleChangeChartType = async (value: AnalyticsChartType) => {
        const update = {
            query: {
                ...query,
                timeDimension: value !== "summary",
            },
            configuration: {
                chartType: value,
            },
        };

        handleUpdate(update);
    };

    const onAdvancedMode = (sqlQuery: string) => {
        handleUpdate({
            query: {
                ...query,
                type: "advanced",
                parameters: [],
                sqlQuery,
            },
        });
    };

    const onBasicMode = () => {
        handleUpdate({
            query: {
                ...query,
                parameters: [],
                type: "stream",
            },
        });
    };

    const onChangeUnit = (value: string) => {
        if (query?.type !== "advanced") {
            return;
        }
        handleUpdate({
            query: {
                ...query,
                parameters: [],
                type: "advanced",
                sqlQuery: sqlText,
                unit: value !== "none" ? value : undefined,
            },
        });
    };

    const picker = (
        <GenericPicker
            deviceIds={query?.deviceIds || []}
            selectedTableColumns={query?.tableColumns || []}
            onReplaceTableColumns={handleChangeTableColumns}
        />
    );

    const xAxisControls = (
        <>
            <Header type="list" />
            <Switch
                state={!!showAxisControls}
                onChange={() => setShowAxisControls(!showAxisControls)}
                label="Show Axis Controls"
                small={true}
                link="https://docs.formant.io/docs/create-an-analytics-view-and-add-modules"
                info="Show or hide the x-axis and partition by controls."
                iconSize="18"
                stateLabels={["OFF", "ON"]}
            />

            {showAxisControls &&
                chartType !== "pie" &&
                chartType !== "summary" && (
                    <>
                        <Label
                            value="X Axis"
                            color={primary.silver}
                            description="Choose the x axis columns for the chart."
                            link="https://docs.formant.io/docs/create-an-analytics-view-and-add-modules"
                        />
                        <SelectField
                            label="X Axis Column"
                            value={
                                params.configuration?.advancedChartSettings
                                    ?.xAxis
                            }
                            onChange={handleChangeXAxis}
                        >
                            {(aggregateLevel === undefined ||
                                aggregateLevel === null ||
                                showAdvanced) && (
                                <option key="none" value={undefined}>
                                    default
                                </option>
                            )}

                            {result?.columns
                                .filter(
                                    _ =>
                                        _.dataType === "string" ||
                                        _.dataType === "timestamp",
                                )
                                ?.map(_ => _.name)
                                .map(_ => (
                                    <option key={_} value={_}>
                                        {_}
                                    </option>
                                ))}
                        </SelectField>
                    </>
                )}

            {showAxisControls && (
                <>
                    <Label
                        value="Partition by"
                        color={primary.silver}
                        description="Choose the column to split by."
                        link="https://docs.formant.io/docs/create-an-analytics-view-and-add-modules"
                    />
                    <SelectField
                        label="Split the data into different segments."
                        value={
                            params.configuration?.advancedChartSettings
                                ?.partitionBy
                        }
                        onChange={handleChangePartitionBy}
                    >
                        <option key="none" value={undefined}>
                            default
                        </option>

                        <option key="unset" value="unset">
                            unset
                        </option>

                        {result?.columns
                            ?.filter(
                                _ =>
                                    _.dataType === "string" ||
                                    _.dataType === "number",
                            )
                            ?.map(_ => _.name)
                            .map(_ => (
                                <option key={_} value={_}>
                                    {_}
                                </option>
                            ))}
                    </SelectField>
                </>
            )}
        </>
    );

    const simpleAdvanctedTabs: ITab[] = [
        {
            name: "Simple",
            content: <SubContainer>{picker}</SubContainer>,
        },
        {
            name: "Advanced",
            content: (
                <SubContainer>
                    <AdvancedEditor
                        moduleId={moduleKey}
                        executeSqlString={executeSqlString}
                        showAdvanced={showAdvanced}
                        chartType={chartType}
                        sqlText={sqlText}
                        onSubmit={onAdvancedMode}
                        deviceIds={query?.deviceIds || []}
                        unit={query?.unit}
                        onChangeUnit={onChangeUnit}
                    >
                        {xAxisControls}
                    </AdvancedEditor>
                </SubContainer>
            ),
        },
    ];

    const tabs = (
        <TabsContainer
            mode="top"
            selected={showAdvanced ? "Advanced" : "Simple"}
            tabs={simpleAdvanctedTabs}
            onSelected={input => {
                if (input === "Advanced") {
                    if (sqlText) {
                        onAdvancedMode(sqlText);
                    }
                    setShowAdvanced(true);
                } else {
                    setShowAdvanced(false);
                    onBasicMode();
                }
            }}
        />
    );

    return (
        <SmallFormContainer>
            <Label
                value="name"
                color={primary.silver}
                link="https://docs.formant.io/docs/create-an-analytics-view-and-add-modules"
                description="Name of this module."
            />
            <TextField value={name} onChange={handleChangeName} small={true} />
            <Label
                value="Visualization"
                color={primary.silver}
                isTitle={false}
                link="https://docs.formant.io/docs/create-an-analytics-view-and-add-modules"
                description="Display style for your data, such as bar chart, line graph or table."
            />
            <ChartSelector
                chartType={chartType}
                onSelectChart={handleChangeChartType}
            />

            {chartType !== "summary" && chartType !== "pie" && (
                <>
                    <Label
                        value="X axis time formatter"
                        color={primary.silver}
                        isTitle={false}
                        link="https://docs.formant.io/docs/create-an-analytics-view-and-add-modules"
                        description="Group data based on specified intervals of time like day of week, week, year, month, date or hour."
                    />
                    <SelectField
                        label="Value"
                        value={aggregateLevel || undefined}
                        onChange={handleChangeAggregateLevel}
                    >
                        {(!aggregateLevel || showAdvanced) && (
                            <option key="none" value={undefined}>
                                none
                            </option>
                        )}

                        {analyticsAggregateLevels.map(_ => (
                            <option key={_} value={_}>
                                {_ === "day" ? "day of week" : _}
                            </option>
                        ))}
                    </SelectField>
                </>
            )}

            {chartType !== "summary" && (
                <>
                    <Header type="list" />
                    <Switch
                        state={!!query?.fillGaps}
                        onChange={handleFillGaps}
                        label="Fill Date Gaps"
                        info="If ON, the x-axis of your visualization will show the entire time interval, whether data is present or not. If OFF, time intervals without data are not included on the chart."
                        link="https://docs.formant.io/docs/create-an-analytics-view-and-add-modules"
                        small={false}
                        iconSize="18"
                        stateLabels={["OFF", "ON"]}
                    />
                </>
            )}
            {allowSplitByDevice && (
                <>
                    <Header type="list" />
                    <Switch
                        state={!!query?.groupByDevice}
                        onChange={handleSplitByDevice}
                        label="Split By Device"
                        small={true}
                        info="Groups data by device."
                        iconSize="18"
                        stateLabels={["OFF", "ON"]}
                    />
                </>
            )}

            {tabs}

            {showAdvanced
                ? simpleAdvanctedTabs[1].content
                : simpleAdvanctedTabs[0].content}
        </SmallFormContainer>
    );
}
